/*
** SetWave tab -- render the Wave tab on the control panel
** Copyright (C) 2021-2026 Tactile Interactive, all rights reserved
*/

import React, {useRef, useEffect} from 'react';
import PropTypes, {checkPropTypes} from 'prop-types';
import {scaleLinear} from 'd3-scale';


import GLScene from '../gl/GLScene.js';
import TextNSlider from '../widgets/TextNSlider.js';
import {getAGroup} from '../utils/storeSettings.js';
import sSettings from '../utils/sSettings.js';

//import {eSpaceCreatedPromise} from '../engine/eEngine.js';
import {interpretCppException} from '../utils/errors.js';

// fixed size GLScene at start.  TODO: also in setVoltageTab
const MINI_WIDTH = 300;
const MINI_HEIGHT = 150;

let traceRegenerate = false;
let traceInteract = false;
let traceEffect = false;
let traceSetRepaint = false;

const propTypes = {
	// actually sets the one in use by the main wave
	saveMainWave: PropTypes.func.isRequired,

	// pararams/settings handed in, as stored in control panel's state
	waveParams: PropTypes.shape({
		// defaults handed in
		waveBreed: PropTypes.oneOf(['circular', 'standing', 'gaussian', 'chord', ]),
		waveFrequency: PropTypes.number,
		pulseWidth: PropTypes.number,
		pulseCenter: PropTypes.number,
	}).isRequired,
	setWaveParams: PropTypes.func,

	// no big deal if it's not constructed yet; we fake it
	space: PropTypes.object,
};


// a component that renders the Set Wave tab, to set a new wave
function SetWaveTab(props) {
	cfpt(propTypes, props);
	let {saveMainWave, waveParams, setWaveParams, space} = props;

	// must remember our own temp wave for minigraph

	const minigraphWaveRef = useRef(null);
	let minigraphWave;
	if (minigraphWaveRef.current) {
		minigraphWave = minigraphWaveRef.current
	}
	else {
		minigraphWave = new eCavity();
		minigraphWave.postConstructor(props.space, 'minigraphWave', null, null);
		minigraphWaveRef.current = minigraphWave;
	}

	const paintingNeedsRef = useRef({cavity: minigraphWave, bumperWidth: 0})
	const paintingNeeds = paintingNeedsRef.current;

	// must remember our repaint func. GLScene ultimately calls setMinigraphRepaint() to hand us
	// the special minigraph repaint function, saved in the minigraphRepaintRef
	const minigraphRepaintRef = useRef(null);
	let minigraphRepaint = minigraphRepaintRef.current;	 // early on, null

	// GLScene will call this when our repaint func is constructed
	function setMinigraphRepaint(repaint) {
		if (traceSetRepaint)
			dblog(`🎨 set mg repaint: `, repaint);
		minigraphRepaintRef.current = minigraphRepaint = repaint;
		minigraphRepaint.sceneName = 'mgRepaint';  // for debugging
	}

	// set the captive miniGraph wave to the new settings,
	// after user changed one. this will do a GL draw.
	function regenerateMiniGraphWave() {
		// set the minigraphWave even if you can't draw it.  Redundant AGAIN!?!?
		minigraphWave.setFamiliarWave(waveParams);

		if (traceRegenerate)
			dblog(`🎨 Regenerating WaveTab minigraph.  params: `, waveParams);

		if (minigraphRepaint) {
			minigraphRepaint(paintingNeeds);
			if (traceRegenerate)
				dblog(`🎨 the minigraph wave after setFamiliarWave() & repaint`,
						minigraphWave);
		}
		else {
			if (traceRegenerate)
				dblog(`🎨  minigraph not painted because no minigraphRepaint()`,
						minigraphWave);
		}
	}

	// set any combination of the wave params, in the Control Panel state.
	// then repaint the minigraph
	function setAndRegenerate(wp) {
		if (traceInteract)
			dblog(`🎨  user interacts: `, wp);
		setWaveParams(wp);

		// set it in our local copy so it draws the latest.  isn't this redundant with setWaveParams?
		waveParams = {...waveParams, ...wp};
		regenerateMiniGraphWave()
	}

	// the local state is just the temporary settings before user clicks Set Wave
	const setBreed = waveBreed => {
		setAndRegenerate({waveBreed});
	}
	const setWaveFrequency = waveFrequency => {
		setAndRegenerate({waveFrequency});
	}
	const setPulseWidth = pulseWidth => {
		setAndRegenerate({pulseWidth});
	}
	const setPulseCenter = pulseCenter => {
		setAndRegenerate({pulseCenter});
	}

	// some sliders disappear for some breeds
	const breed = waveParams.waveBreed;
	const needPulseWidth = breed == 'gaussian' || breed == 'chord';
	const needOffset = (breed == 'gaussian' || breed == 'chord');
	const alongThe = (breed == 'gaussian')
		? 'pulse width'
		: "whole cavity length"

	// don't let them set a freq to Nyquist or beyond
	let highestFrequency = space.nStates / 2 - 1;
	highestFrequency = Math.min(
		sSettings.minMaxes.waveParams.waveFrequency.max, highestFrequency);
	let moderateFrequency = Math.round(highestFrequency * .3);

	// sliders for each param besides breed
	const waveParamSliders = <>
		<TextNSlider className='waveFrequency' label='frequency'
			value={+waveParams.waveFrequency}
			min={-highestFrequency}
			max={highestFrequency}
			sMin={-moderateFrequency}
			sMax={moderateFrequency}
			step={'standing' == breed ? .5 : 1}
			handleChange={setWaveFrequency}
			title={`how many cycles your wave should have, along the ${alongThe}`}
		/>

		<TextNSlider className='pulseWidth' label='pulse width, %'
			style={{display: needPulseWidth ? 'block' :	 'none'}}
			value={+waveParams.pulseWidth}
			min={sSettings.minMaxes.waveParams.pulseWidth.min}
			max={sSettings.minMaxes.waveParams.pulseWidth.max}
			step={.1}
			handleChange={setPulseWidth}
			title="how fat your	 wave packet should be, percent of whole cavity length"
		/>

		<TextNSlider className='offset' label='offset, %'
			style={{display: needOffset ? 'block' :	 'none'}}
			value={+waveParams.pulseCenter}
			min={sSettings.minMaxes.waveParams.pulseCenter.min}
			max={sSettings.minMaxes.waveParams.pulseCenter.max}
			step={2}
			handleChange={setPulseCenter}
			title="where do you want the center of the hump to be, from left side"
		/>
	</>;

	// the minigraph
	const glScene = <GLScene
		space={space}
		sceneClassName='flatScene' sceneName='swMiniGraph'
		canvasInnerWidth={MINI_WIDTH}
		canvasInnerHeight={MINI_HEIGHT}
		setGLRepaint={setMinigraphRepaint}
		paintingNeeds={paintingNeeds}
		title="preview of what your wave will look like after clicking Set Wave"
	/>;

	// radio buttons
	const breedSelector = <div className='breedSelector'>
		<label title='a spiral wave, same magnitude everywhere'>
			circular
			<input type='radio' checked={'circular' == breed} name='circular'
				onChange={ev => setBreed('circular')} />
		</label>

		<label title='two identical waves traveling in opposite directions, superimposed'>
			standing
			<input type='radio'	 checked={'standing' == breed} name='standing'
				onChange={ev => setBreed('standing')} />
		</label>

		<label title='a typical wave packet'>
			gauss pulse
			<input type='radio'	 checked={'gaussian' == breed} name='gaussian'
				onChange={ev => setBreed('gaussian')} />
		</label>

		{/* sorry don't have time to fix this now	  <label>
			chord pulse
			<input type='radio'	 checked={'chord' == breed}
				onChange={ev => setBreed('chord')} />
		</label>*/}
	</div>;

	// this will happen after the render
	useEffect(() => {
		if (traceEffect)
			dblog(`🎨 useEffect triggers regen of gl`);
		regenerateMiniGraphWave();
		return () => {};
	});

	return <div className='SetWaveTab  controlPanelPanel'
			title="Use this tab to set the main wave, and save these parameters for next time.">
		<h3>Design a new Wave</h3>
		<div className='waveTabCol '>
			{breedSelector}
			<button className='lite setWaveButton'
					onClick={ev => saveMainWave(waveParams)}
					title='start using it' >
				Set Wave
			</button>
		</div>

		<div className='waveTabCol'>
			<div className='waveMiniGraph'>
				{glScene}
			</div>
		</div>

		<div className='waveTabCol waveParamSliders'>
			{waveParamSliders}
		</div>
	</div>;
}

export default SetWaveTab;
